/**
 * LOOKING ONE SUBJECT UP — four lenses on one screen.
 *
 *   "EVERY BIT OF DATA READ AND WRITTEN, ON EVERY PROFILE'S SCREEN, must be
 *    viewable by staff with the necessary permissions."
 *
 * One component rather than four, because the SHAPE of the work is identical
 * every time: you hold a code, you say why, the database writes the reason down
 * and then answers. What differs is which reads the lens offers, and that is
 * data — LENSES below — not four copies of a form.
 *
 * WHY THE LENS DECIDES WHAT IS OFFERED
 *
 * Support Centre gets the dossier. Finance gets the money and the seats.
 * Internal Audit gets the score workings, the trust trail, and who at Orchid has
 * been looking. Legal gets the conversation. None of them can reach another
 * lens's reads by opening this screen from their own card, because the read
 * itself is gated in the database — but offering a button that will certainly
 * refuse is not security, it is furniture. So the lens draws only what its card
 * can actually open.
 *
 * WHAT IS ON THE SCREEN BEFORE ANYTHING IS FETCHED
 *
 * The sentence about what this costs. Somebody about to open a customer's whole
 * record should read "this is written to the activity log under your name and
 * cannot be edited afterwards" BEFORE they type, not in a toast after. It is the
 * same reasoning as Canon 4.2 itself, applied to the human rather than the row.
 */

import { useCallback, useState } from 'react';
import {
  O, Card, StatusPill, Notice, PrimaryCTA, QuietButton, Field, textInputStyle, Spinner,
} from '../ui';
import {
  fetchSubject, fetchDossier, fetchDocumentShelf, fetchReputationWorkings,
  fetchTrustTrail,
  fetchTrustStanding, fetchMoneyTrail, fetchSeatRoster, fetchWhoLooked, fetchConversation,
  fetchJobHistory,
  fetchComplianceStanding,
  groupSections, MIN_REASON, tidyCode, looksLikeACode,
} from '../lib/subject';

/**
 * What each card's version of this screen opens.
 *
 * `reads` are drawn one after another, each with its own heading. `subjectKey`
 * says whether the lens is addressed by a person/business code or by a job
 * reference, which is the one genuine difference in the form itself.
 */
export const LENSES = {
  subject_summary: {
    title: 'Look somebody up',
    blurb: 'Everything this person or business sees on their own screens, in the sections '
         + 'their screens put it in. No identity number, no account number, no message body '
         + 'and no document file — each of those has its own permission.',
    subjectKey: 'code',
    reads: [
      { key: 'dossier', heading: 'The record', run: fetchDossier, render: 'dossier' },
      // SECOND, AND ONLY FOR A BUSINESS. "why am I not scoring higher on
      // Compliance and Certification" is the commonest question a provider
      // rings up with, and until script 163 nobody at Orchid could answer it
      // either -- one of the five requirements was scored, unwritten and
      // unlisted. It refuses for a subject with no business, which the
      // renderer shows as the database's own sentence rather than an error.
      { key: 'compliance', heading: 'Why their compliance score is what it is',
        run: fetchComplianceStanding, render: 'compliance' },
    ],
    elsewhere: 'The money is in Finance, the score workings and the audit trail are in '
             + 'Internal Audit, and a job conversation is in Legal & Disputes.',
  },

  subject_finance: {
    title: 'A subject’s money',
    blurb: 'Subscriptions, collections, invoices, rent and mandate events — and the seats '
         + 'this business is billed for. Account numbers are masked; the encrypted one is '
         + 'opened only by the mandate export, which logs every decryption.',
    subjectKey: 'code',
    reads: [
      { key: 'money', heading: 'Money', run: fetchMoneyTrail, render: 'money' },
      { key: 'seats', heading: 'Seats', run: fetchSeatRoster, render: 'seats' },
    ],
  },

  subject_trust: {
    title: 'A subject’s standing',
    blurb: 'Why a score is what it is, what the platform recorded happening, and who at '
         + 'Orchid has looked at this subject. The weights themselves are nowhere here: a '
         + 'component total is this subject’s own result, the divisor is the model.',
    subjectKey: 'code',
    reads: [
      // THE LEVEL FIRST, THEN WHY THE SCORE IS WHAT IT IS. A desk looking a
      // subject up on this lens is answering "can this person do the thing they
      // are asking to do", and the level IS that answer; the workings are the
      // supporting detail. It also puts the newest permission first, so a desk
      // that holds view_trust_events but not scores_view meets an empty first
      // section that says which permission it would need.
      { key: 'standing', heading: 'Trust level',                 run: fetchTrustStanding,      render: 'standing' },
      { key: 'workings', heading: 'Why the score is what it is', run: fetchReputationWorkings, render: 'workings' },
      { key: 'trail',    heading: 'Trust events',                run: fetchTrustTrail,         render: 'trail' },
      { key: 'looked',   heading: 'Who has looked at this subject', run: fetchWhoLooked,       render: 'looked' },
    ],
  },

  job_conversation: {
    title: 'Read a job',
    blurb: 'One job by its reference: what was quoted, when the provider said they would '
         + 'arrive, every price change, what evidence was captured, what was paid, and the '
         + 'whole conversation. Never a person — one job reference.',
    subjectKey: 'job',
    reads: [
      // HISTORY FIRST, THREAD SECOND, and that order is the answer to the
      // question this screen exists for. A customer asking "why was I charged
      // this" wants the quote and the revisions; the conversation is the
      // supporting detail. It also puts the cheaper permission first: the
      // history needs view_job_history, the thread still needs resolve_dispute,
      // so a first-line agent sees the facts and an empty second section
      // explaining what they would need to read the messages.
      { key: 'history', heading: 'What happened', run: fetchJobHistory, render: 'history' },
      { key: 'thread', heading: 'The conversation', run: fetchConversation, render: 'thread' },
    ],
  },

  subject_documents: {
    title: 'A subject’s documents',
    blurb: 'The files themselves: identity documents, proof of address, provider certificates '
         + 'and job evidence. Requires an authenticator, because view_kyc_document does.',
    subjectKey: 'code',
    reads: [{ key: 'shelf', heading: 'Documents on file', run: fetchDocumentShelf, render: 'shelf' }],
  },
};

const eyebrow = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: O.inkFaint, margin: '0 0 10px',
};

const Meta = ({ children }) => (
  <p style={{ margin: '4px 0 0', fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted }}>{children}</p>
);

/** One row of the dossier: a label, the fact, and the sentence under it. */
const stamp = (d) =>
  new Date(d).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });

const FactRow = ({ label, value, detail, reference, when, claim, claimWhen }) => (
  <div style={{ padding: '10px 0', borderTop: `1px solid ${O.hairline}` }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: O.ink }}>{label}</span>
      {value && <StatusPill tone="quiet">{value}</StatusPill>}
      {reference && (
        <span style={{ fontSize: 11, color: O.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
          {reference}
        </span>
      )}
    </div>
    {detail && <Meta>{detail}</Meta>}
    {when && (
      <p style={{ margin: '3px 0 0', fontSize: 11, color: O.inkFaint }}>
        {stamp(when)}
      </p>
    )}

    {/* WHAT THEY SAID THIS DOCUMENT WOULD SAY.
        Set apart by a rule rather than a colour, because a colour would read
        as a verdict and there is none here — see the shelf renderer below. */}
    {claim && (
      <div style={{
        marginTop: 8, paddingLeft: 10,
        borderLeft: `2px solid ${O.hairlineOn}`,
      }} data-testid="fact-claim">
        <p style={{ margin: 0, fontSize: 11, color: O.inkFaint }}>
          They typed this address
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 13, lineHeight: 1.6, color: O.ink }}>
          {claim}
        </p>
        {claimWhen && (
          <p style={{ margin: '3px 0 0', fontSize: 11, color: O.inkFaint }}>
            recorded {stamp(claimWhen)}
          </p>
        )}
      </div>
    )}
  </div>
);

const Empty = ({ children }) => (
  <p style={{ fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted, margin: '8px 0 0' }}>{children}</p>
);

/**
 * The renderers, one per read shape.
 *
 * Each is handed the rows the database returned and nothing else. None of them
 * filters, hides or recomputes anything: if a row came back, it is on the
 * screen. A component that quietly dropped a row would be the exact failure this
 * whole task exists to prevent — data that is in the database, permitted, and
 * still not visible to the person who needs it.
 */
const RENDERERS = {
  /**
   * A job, in the order it happened.
   *
   * Grouped by nothing: the database returned it in time order and time order
   * is the answer. Grouping by section would put three price revisions
   * together and separate them from the arrival they followed, which is the
   * shape of the data rather than the shape of the story.
   */
  history: (rows) => {
    if (!rows.length) return <Empty>Nothing recorded on this job.</Empty>;
    if (rows.length === 1 && rows[0].section === 'not_found') {
      return <Empty>{rows[0].headline}. {rows[0].detail}</Empty>;
    }
    return rows.map((row, i) => (
      <FactRow
        key={i}
        label={row.headline}
        value={row.section}
        detail={row.detail}
        reference={row.reference}
        when={row.occurred_at}
      />
    ));
  },

  dossier: (rows) => {
    const sections = groupSections(rows);
    if (!sections.length) return <Empty>Nothing on file for this subject yet.</Empty>;
    return sections.map((s) => (
      <div key={s.section} style={{ marginTop: 18 }}>
        <h4 style={eyebrow}>{s.section} · {s.rows.length}</h4>
        {s.rows.map((row, i) => (
          <FactRow
            key={`${s.section}-${i}`}
            label={row.label}
            value={row.value}
            detail={row.detail}
            reference={row.reference}
            when={row.occurred_at}
          />
        ))}
      </div>
    ));
  },

  money: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={`${row.stream} · ${row.label}`}
      value={row.amount === null || row.amount === undefined
        ? row.status
        : `R${Number(row.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })} — ${row.status}`}
      detail={row.detail}
      reference={row.reference}
      when={row.moved_at}
    />
  )) : <Empty>No money has moved on this subject.</Empty>),

  seats: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={`${row.holder} · ${row.seat_role}`}
      value={`${row.seat_kind} — ${row.status}${row.billable ? ' · billable' : ''}`}
      detail={row.detail}
      when={row.since}
    />
  )) : <Empty>No seats on this business.</Empty>),

  /**
   * One row, or none. The level is a rung on a named ladder rather than a
   * percentage, so it is stated as words and a position — the Portal has no
   * ring component and inventing one here would put a second visualisation of
   * a score on the platform, which §6 forbids.
   *
   * fraudulent_findings and has_eviction appear here and NOT on the member's
   * own screen. They are what a compliance desk is looking the subject up in
   * order to check, and the member's screen is not the place to argue with
   * them.
   */
  standing: (rows) => {
    const r = rows[0];
    if (!r) return <Empty>No trust level has been computed for this subject.</Empty>;
    return (
      <>
        <FactRow
          label="Level"
          value={`${r.level_name} (${r.level} of 6)`}
          detail={r.next_level_name
            ? `Next rung: ${r.next_level_name}`
            : 'This is the top rung.'}
        />
        <FactRow
          label="Trust confidence"
          value={r.confidence_pct === null ? '—' : `${r.confidence_pct}%`}
          detail={r.confidence_band}
        />
        <FactRow
          label="Verified / evidence"
          value={`${r.verification_rate_pct ?? '—'}% · ${r.evidence_density_pct ?? '—'}%`}
          detail={r.evidence_blocked_months
            ? `${r.evidence_blocked_months} month(s) where evidence could not be produced`
            : 'No months where evidence was blocked'}
        />
        <FactRow
          label="Confirmed payments"
          value={String(r.confirmed_payments)}
          detail={`Tenure ${r.tenure_months} month(s)`}
        />
        {(r.has_eviction || r.fraudulent_findings > 0) && (
          <FactRow
            label="On file against this subject"
            value={[r.has_eviction ? 'Eviction' : null,
                    r.fraudulent_findings > 0
                      ? `${r.fraudulent_findings} fraudulent finding(s)` : null]
                    .filter(Boolean).join(' · ')}
            detail="These hold the level where it is regardless of the score."
          />
        )}
        {r.blocked_by && (
          <FactRow
            label="Level is held by"
            value={r.blocked_by === 'both' ? 'PRR score and evidence' : r.blocked_by.replace(/_/g, ' ')}
            detail="Not by anything outstanding the subject could submit."
          />
        )}
      </>
    );
  },

  workings: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={`${row.scale} · ${row.component}`}
      value={row.points === null ? '—' : String(row.points)}
      detail={row.note}
    />
  )) : <Empty>No score has been computed for this subject.</Empty>),

  trail: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={row.event_type}
      value={`${row.role_here} · ${row.channel}`}
      detail={row.detail}
      when={row.emitted_at}
    />
  )) : <Empty>Nothing has been recorded against this subject.</Empty>),

  looked: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={row.who || 'a staff account'}
      value={row.action}
      detail={`“${row.reason}”`}
      reference={row.seat}
      when={row.looked_at}
    />
  )) : <Empty>Nobody at Orchid has opened this subject’s record. That is the ordinary case.</Empty>),

  compliance: (rows) => {
    if (!rows.length) return <Empty>No compliance score for this subject.</Empty>;
    const open = rows.filter((r) => r.applies && !r.satisfied);
    return (
      <>
        <Meta>
          {rows[0].points} of {rows[0].ceiling}.{' '}
          {open.length === 0
            ? 'Everything that applies to this business is in place — this part of '
              + 'their score is as high as it goes, and that is the honest answer to give them.'
            : `${open.length} outstanding. Each row says what it is waiting on.`}
        </Meta>
        {rows.map((row) => (
          <FactRow
            key={row.item_key}
            label={row.title}
            value={!row.applies ? 'does not apply' : (row.satisfied ? 'met' : `outstanding · worth ${row.weight}`)}
            detail={row.what_to_do}
          />
        ))}
      </>
    );
  },

  /*
   * THE DOCUMENT, AND WHAT THE PERSON SAID IT WOULD SAY.
   *
   * The founder, after handing in his own proof of address:
   *
   *   "one can attach a pdf, but there's nothing in the process or on the
   *    platform that automatically reads the address on that document and
   *    there's no typed user residential address to compare it to to see if
   *    it's correct and lines up with the attached document."
   *
   * Script 270 gave a person somewhere to type it. Script 273 put it on THIS
   * row — beside the document, not under a separate heading, because a desk
   * comparing two things reads them side by side or does not compare them.
   *
   * ORCHID DOES NOT READ THE PDF and this does not pretend otherwise. There is
   * no match, no tick, no colour and no score here. The claim is shown, the
   * document is shown, and the person at the desk is the thing that decides —
   * which is Immutable Rule 4 at the last moment it could be broken, and it
   * would be broken worse here than anywhere, because a verdict rendered on a
   * compliance screen carries the desk's authority without its judgement.
   *
   * The timestamp is not decoration either: a claim typed AFTER the document
   * was uploaded is a different fact from one typed before it, and only a
   * human can weigh which.
   */
  shelf: (rows) => (rows.length ? rows.map((row, i) => (
    <FactRow
      key={i}
      label={row.document_kind}
      value={row.status}
      detail={row.storage_path}
      reference={row.reference}
      when={row.submitted_at}
      claim={row.claim}
      claimWhen={row.claim_recorded_at}
    />
  )) : <Empty>No documents on file.</Empty>),

  thread: (rows) => (rows.length ? rows.map((row, i) => (
    <div key={i} style={{ padding: '10px 0', borderTop: `1px solid ${O.hairline}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: O.ink }}>{row.sender}</span>
        {row.is_system && <StatusPill tone="quiet">System</StatusPill>}
        <span style={{ fontSize: 11, color: O.inkFaint }}>
          {new Date(row.sent_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      </div>
      <p style={{ margin: '5px 0 0', fontSize: 13, lineHeight: 1.7, color: O.ink, whiteSpace: 'pre-wrap' }}>
        {row.body}
      </p>
    </div>
  )) : <Empty>Nobody said anything on this job.</Empty>),
};

export default function SubjectLookup({ lens = 'subject_summary' }) {
  const spec = LENSES[lens] || LENSES.subject_summary;
  const byJob = spec.subjectKey === 'job';

  const [subjectRef, setSubjectRef] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [header, setHeader] = useState(null);
  const [results, setResults] = useState(null);

  const tooShort = reason.trim().length < MIN_REASON;
  const noSubject = subjectRef.trim().length === 0;

  // A code that is not shaped like a code is almost always a name typed into
  // the wrong box, and saying so here is kinder than sending it to a database
  // that will correctly refuse to know who "Thandi" is.
  const wrongShape = !byJob && subjectRef.trim().length > 0 && !looksLikeACode(subjectRef);

  const open = useCallback(async () => {
    setBusy(true);
    setError(null);
    setHeader(null);
    setResults(null);

    const subject = byJob ? subjectRef.trim() : tidyCode(subjectRef);
    const why = reason.trim();

    // The header first, and only on the lenses addressed by a subject code. It
    // is its own logged read, which is correct: knowing WHO a code belongs to is
    // a disclosure, and a screen that got the name for free before the reason
    // was accepted would be the leak this whole design closes.
    if (!byJob) {
      const head = await fetchSubject(subject, why);
      if (head.error) { setError(head.error); setBusy(false); return; }
      setHeader((head.data || [])[0] || null);
    }

    const gathered = [];
    for (const read of spec.reads) {
      // eslint-disable-next-line no-await-in-loop
      const res = await read.run(subject, why);
      gathered.push({ ...read, rows: res.data || [], error: res.error });
    }
    setResults(gathered);
    setBusy(false);
  }, [byJob, subjectRef, reason, spec.reads]);

  return (
    <>
      <h2 style={eyebrow}>{spec.title}</h2>
      <p style={{ fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted, margin: '0 0 14px' }}>
        {spec.blurb}
      </p>

      <Card>
        <Field
          label={byJob ? 'Job reference' : 'OFX Connect Code or business code'}
          hint={byJob
            ? 'The reference on the job, e.g. JOB-260826-GREKZI2.'
            : 'The code the person reads to you off their own screen. Not their name.'}
          htmlFor="subject-ref"
        >
          <input
            id="subject-ref"
            value={subjectRef}
            onChange={(e) => setSubjectRef(e.target.value)}
            placeholder={byJob ? 'JOB-260826-GREKZI2' : 'OFX-7P8QY3V9C'}
            autoComplete="off"
            spellCheck={false}
            style={{ ...textInputStyle(false), textTransform: byJob ? 'none' : 'uppercase' }}
          />
        </Field>

        {wrongShape && (
          <p style={{ margin: '-4px 0 12px', fontSize: 11.5, color: O.inkFaint }}>
            Codes begin with OFX. If that is a name, there is deliberately no way to
            search for it — you need the code from the person themselves.
          </p>
        )}

        <Field
          label="Why are you opening this record?"
          hint="Written to the append-only activity log under your name, before anything is read, and not editable afterwards."
          htmlFor="subject-reason"
        >
          <textarea
            id="subject-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="responding to ticket #4821"
            style={{ ...textInputStyle(false), resize: 'vertical' }}
          />
        </Field>

        {tooShort && reason.length > 0 && (
          <p style={{ margin: '-4px 0 12px', fontSize: 11.5, color: O.inkFaint }}>
            A few more words — at least {MIN_REASON} characters.
          </p>
        )}

        <PrimaryCTA onClick={open} busy={busy} disabled={busy || tooShort || noSubject}>
          {busy ? 'Opening…' : 'Record the reason and open'}
        </PrimaryCTA>

        {results && (
          <div style={{ marginTop: 12, maxWidth: 200 }}>
            <QuietButton onClick={() => { setResults(null); setHeader(null); setError(null); }}>
              Close this record
            </QuietButton>
          </div>
        )}
      </Card>

      {error && <div style={{ marginTop: 14 }}><Notice tone="critical">{error}</Notice></div>}

      {busy && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner /><span style={{ fontSize: 12.5, color: O.inkMuted }}>Reading…</span>
        </div>
      )}

      {header && (
        <div style={{ marginTop: 14 }}>
          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: O.ink, margin: 0 }}>
                {header.display_name}
              </h3>
              <StatusPill tone="quiet">{header.reference}</StatusPill>
              <StatusPill tone={header.account_state === 'active' ? 'green' : 'warning'}>
                {header.account_state}
              </StatusPill>
            </div>
            <Meta>{header.headline}</Meta>
            <Meta>
              Onboarding: {header.onboarding_state}
              {header.joined_at
                ? ` · joined ${new Date(header.joined_at).toLocaleDateString('en-ZA', { dateStyle: 'medium' })}`
                : ''}
            </Meta>
          </Card>
        </div>
      )}

      {results && results.map((read) => (
        <div key={read.key} style={{ marginTop: 14 }}>
          <Card>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
              {read.heading}
            </h3>
            {read.error
              ? <div style={{ marginTop: 10 }}><Notice tone="warning">{read.error}</Notice></div>
              : RENDERERS[read.render](read.rows)}
          </Card>
        </div>
      ))}

      {results && spec.elsewhere && (
        <p style={{ marginTop: 14, fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint }}>
          {spec.elsewhere}
        </p>
      )}
    </>
  );
}
